/**
 * prefetch.ts — Route-level data + chunk prefetching
 *
 * Two strategies:
 * 1. warmCoreData(): called on AppShell mount, prefetches all tab data
 * 2. prefetchRoute(): called on link hover, prefetches page chunk + data
 *
 * @see Issue #618
 */
import { getSupabase, waitForAuth } from './supabase';
import { prefetch } from './query-cache';
import { createLogger } from './logger';

const log = createLogger('Prefetch');

/** Chunks already prefetched (avoid duplicate link preloads) */
const prefetchedChunks = new Set<string>();

/**
 * Warm core page data in background on app boot.
 * Called once from AppShell.
 * #710: Waits for auth ready before querying to avoid RLS failures.
 */
export function warmCoreData(): void {
  // Gate behind auth to prevent bare-anon queries
  waitForAuth().then(() => {
    const sb = getSupabase();

  // Topics list (first page)
  prefetch('topics:all:20:0', async () => {
    const { data, count } = await sb
      .from('topics')
      .select('*, topic_atom_links(count)', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(0, 19);
    const topics = (data ?? []).map((t: Record<string, unknown>) => {
      const linkArr = t.topic_atom_links as Array<{ count: number }> | undefined;
      const atom_count = Array.isArray(linkArr) ? linkArr[0]?.count ?? 0 : 0;
      const { topic_atom_links: _, ...rest } = t;
      return { ...rest, atom_count };
    });
    return { topics, total: count ?? 0 };
  });

  // Ranked feed (first page — warms the main feed)
  const seed = Math.floor(Date.now() / 3600000) % 10000;
  prefetch(`ranked-feed:anon:10:0:${seed}`, async () => {
    const { data } = await sb.rpc('get_ranked_feed', {
      p_user_id: '',
      p_limit: 10,
      p_offset: 0,
      p_seen_csv: '',
      p_seed: seed,
    });
    return { atoms: data ?? [], total: (data ?? []).length };
  });

  // Atoms (for follow page)
  prefetch('atoms:all:all:20:0', async () => {
    const { data, count } = await sb
      .from('idea_atoms')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .neq('visibility', 'anonymous')
      .range(0, 19);
    return { atoms: data ?? [], total: count ?? 0 };
  });

  log.info('Core data warm started');
  }); // end waitForAuth().then()
}

/**
 * Prefetch a route's JS chunk by injecting a <link rel="prefetch">.
 * Uses modulepreload for modern browsers.
 */
export function prefetchChunk(chunkHref: string): void {
  if (prefetchedChunks.has(chunkHref)) return;
  prefetchedChunks.add(chunkHref);

  const link = document.createElement('link');
  link.rel = 'modulepreload';
  link.href = chunkHref;
  document.head.appendChild(link);
}

/** Map route paths to their lazy chunk imports */
const ROUTE_CHUNKS: Record<string, () => Promise<unknown>> = {
  '/follow': () => import('../pages/FollowPage'),
  '/topics': () => import('../pages/TopicsPage'),
  '/club': () => import('../pages/ClubPage'),
  '/ai-talk': () => import('../pages/AiTalkPage'),
};

/**
 * Called on <Link> hover/focus — prefetch chunk + data.
 */
export function prefetchRoute(path: string): void {
  // Normalize: strip query params, hash
  const clean = path.split('?')[0].split('#')[0];

  // Prefetch JS chunk
  const chunkLoader = ROUTE_CHUNKS[clean];
  if (chunkLoader && !prefetchedChunks.has(clean)) {
    prefetchedChunks.add(clean);
    chunkLoader().catch(() => {}); // Pre-execute the dynamic import
  }

  // Prefetch data — no-op: data is already in cache from warmCoreData or previous visit
}
